/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useState, useEffect, SyntheticEvent, useCallback } from "react";
import {
  JobSiteNameListProjectsInner,
  JobsiteSummaryType,
  MapSummary,
} from "client";
import {
  Dialog,
  Typography,
  DialogActions,
  useTheme,
  Grid,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  styled,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ErrorDialog } from "components/Common/ErrorDialog";
import { BorderedButton } from "components/Common/BorderedButton";
import { useGetProject } from "hooks/GetProjectHook";
import { SpinnerDelay } from "components/Common/SpinnerDelay";
import { ProjectInfo, useUserContext } from "context/UserContext";
import moment from "moment";
import { useGetJobsiteMap } from "hooks/GetJobsiteMapHook";

export interface ProjectListProps {
  open?: boolean;
  onSelected: (jobsite?: ProjectInfo) => void;
}

export const ProjectList: FC<ProjectListProps> = ({
  open = true,
  onSelected,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { jobsite } = useUserContext();

  const { retry, loading, error, value } = useGetProject();
  const jobsiteMapApi = useGetJobsiteMap();
  const excuteJobsiteMapApi = jobsiteMapApi.setTargetJobsiteId;
  const [projectSelected, setProjectSelected] = useState(false);
  const [geosites, setGeosites] = useState<Array<JobSiteNameListProjectsInner>>(
    []
  );
  const [jobsites, setJobsites] = useState<
    Array<JobsiteSummaryType> | undefined
  >([]);
  const [mapInfos, setMapInfos] = useState<Array<MapSummary>>([]);
  const [errMsg, setErrMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const [geositeSelected, setGeositeSelected] = useState<
    JobSiteNameListProjectsInner | undefined
  >(jobsite);
  const [jobsiteSelected, setJobsiteSelected] = useState(jobsite?.jobSiteId);
  const [mapInfoSelected, setMapInfoSelected] = useState(jobsite?.mapId);

  // Select geosite
  const handleGeositeListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    geosite: JobSiteNameListProjectsInner
  ) => {
    setGeositeSelected(geosite);
    // if jobsite or mapName selected ...
    if (jobsiteSelected || mapInfoSelected) {
      setJobsites([]);
      setMapInfos([]);
      setJobsiteSelected(undefined);
      setMapInfoSelected(undefined);
    }
    if (geosite.jobSites && geosite.jobSites.length > 0) {
      const sorted = [...geosite.jobSites].sort((a, b) => compareJobsite(a, b));
      setJobsites(sorted);
    } else {
      setErrMsg(t("AWVUI-MSG21"));
      setShowError(true);
    }
  };

  // Select jobsite
  const handleJobsiteListItemClick = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    selectedJobsite: JobsiteSummaryType
  ) => {
    setJobsiteSelected(selectedJobsite.jobSiteId);
    // if mapName selected ...
    if (mapInfoSelected) {
      setMapInfoSelected(undefined);
      setMapInfos([]);
    }
    excuteJobsiteMapApi(selectedJobsite.jobSiteId);
  };

  // Select MapInfo
  const handleMapInfoListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    mapInfo: MapSummary
  ) => {
    setMapInfoSelected(mapInfo.mapId);
  };

  // OK Button
  const handleClose = (event: SyntheticEvent<unknown>, reason?: string) => {
    if (geositeSelected && jobsiteSelected && mapInfoSelected) {
      const jobSiteName = jobsites?.find(
        (site) => site.jobSiteId === jobsiteSelected
      )?.jobSiteName;
      const selectedProject: ProjectInfo = {
        geoSiteId: geositeSelected.geoSiteId,
        geoSiteName: geositeSelected.geoSiteName,
        latitude: geositeSelected.latitude,
        longitude: geositeSelected.longitude,
        zoom: geositeSelected.zoom,
        timezone: geositeSelected.timezone,
        jobSites: geositeSelected.jobSites,
        jobSiteId: jobsiteSelected,
        jobSiteName: jobSiteName,
        mapId: mapInfoSelected,
        organizationId: geositeSelected.organizationId,
      };
      moment.tz.setDefault(geositeSelected?.timezone);
      onSelected(selectedProject);
      setProjectSelected(true);
    }
  };

  // reset selection and list
  const init = () => {
    setGeosites([]);
    setJobsites([]);
    setMapInfos([]);
    setGeositeSelected(undefined);
    setJobsiteSelected(undefined);
    setMapInfoSelected(undefined);
    excuteJobsiteMapApi(undefined);
  };

  // sort geosites
  const compareGeosite = (
    a: JobSiteNameListProjectsInner,
    b: JobSiteNameListProjectsInner
  ) => {
    if (a.geoSiteId && b.geoSiteId) {
      return a.geoSiteId.localeCompare(b.geoSiteId);
    } else {
      return 1;
    }
  };

  // sort jobsites
  const compareJobsite = (a: JobsiteSummaryType, b: JobsiteSummaryType) => {
    if (a.jobSiteId && b.jobSiteId) {
      return a.jobSiteId.localeCompare(b.jobSiteId);
    } else {
      return 1;
    }
  };

  // sort mapinfos
  const compareMapInfo = (a: MapSummary, b: MapSummary) => {
    if (a.mapId && b.mapId) {
      return a.mapId.localeCompare(b.mapId);
    } else {
      return 1;
    }
  };

  const isSettingProject = useCallback(() => {
    return jobsite?.geoSiteId && jobsite.jobSiteId && jobsite.mapId;
  }, [jobsite]);

  useEffect(() => {
    if (value) {
      if (projectSelected) {
        const geosite = value.projects?.find(
          (p) => p.geoSiteId === jobsite?.geoSiteId
        );
        if (geosite) {
          moment.tz.setDefault(geosite?.timezone);
          const newProject: ProjectInfo = {
            geoSiteId: geosite.geoSiteId,
            geoSiteName: geosite.geoSiteName,
            latitude: geosite.latitude,
            longitude: geosite.longitude,
            zoom: geosite.zoom,
            timezone: geosite.timezone,
            jobSites: geosite.jobSites,
            jobSiteId: jobsite?.jobSiteId ?? "",
            jobSiteName: jobsite?.jobSiteName,
            mapId: jobsite?.mapId ?? "",
            organizationId: jobsite?.organizationId,
          };
          onSelected(newProject);
        } else {
          setProjectSelected(false);
        }
      }
    }
  }, [projectSelected, value, jobsite, onSelected]);

  useEffect(() => {
    (async () => {
      if (value) {
        if (value.projects && !projectSelected) {
          const sorted = [...value.projects].sort((a, b) =>
            compareGeosite(a, b)
          );
          setGeosites(sorted);
        }
        if (isSettingProject()) {
          // set selected geosite
          setGeositeSelected(jobsite);
          // set jobsiteList and selected jobsite
          const jobSiteList = jobsite?.jobSites ?? [];
          const sortedjobSites = [...jobSiteList].sort((a, b) =>
            compareJobsite(a, b)
          );
          setJobsites(sortedjobSites);
          setJobsiteSelected(jobsite?.jobSiteId);
          // set mapList and selected map
          if (jobsite?.jobSiteId) {
            excuteJobsiteMapApi(jobsite.jobSiteId);
            setMapInfoSelected(jobsite.mapId);
          }
        }
      }
    })();
  }, [value, jobsite, projectSelected, isSettingProject, excuteJobsiteMapApi]);

  // after excute getjobsiteMaps
  useEffect(() => {
    if (jobsiteMapApi.value) {
      if (jobsiteMapApi.value.length > 0) {
        const sorted = [...jobsiteMapApi.value].sort((a, b) =>
          compareMapInfo(a, b)
        );
        setMapInfos(sorted);
        excuteJobsiteMapApi(undefined);
      } else {
        setErrMsg(t("AWVUI-MSG186"));
        setShowError(true);
      }
    }
  }, [jobsiteMapApi.value, t, excuteJobsiteMapApi]);

  useEffect(() => {
    setErrMsg(t("AWVUI-MSG185"));
    setShowError(!!error);
  }, [error, t]);

  useEffect(() => {
    setErrMsg(t("AWVUI-MSG186"));
    setShowError(!!jobsiteMapApi.error);
  }, [jobsiteMapApi.error, t]);

  useEffect(() => {
    if (geositeSelected && jobsiteSelected && mapInfoSelected) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [geositeSelected, jobsiteSelected, mapInfoSelected]);

  return (
    <>
      {(loading || jobsiteMapApi.loading) && <SpinnerDelay />}
      {!projectSelected && (
        <Dialog
          disableEscapeKeyDown
          open={open}
          sx={{
            "& .MuiDialog-paper": {
              minWidth: "680px",
              minHeight: "340px",
              paddingTop: "20px",
              bgcolor: theme.palette.grey[400],
              justifyContent: "center",
              textAlign: "center",
            },
          }}
        >
          <Grid container padding="0px 20px 10px 20px">
            <CustomGrid item xs={4}>
              <Typography variant="h5">{t("AWVUI-MSG183")}</Typography>
              <CustomList>
                {geosites.map((item, index) => (
                  <div key={`wrap-${index}`}>
                    <ListItemButton
                      key={index}
                      selected={item.geoSiteId === geositeSelected?.geoSiteId}
                      onClick={(event) =>
                        handleGeositeListItemClick(event, item)
                      }
                    >
                      <ListItemText primary={item.geoSiteName} />
                    </ListItemButton>
                    <Divider />
                  </div>
                ))}
              </CustomList>
            </CustomGrid>
            <CustomGrid item xs={4}>
              <Typography variant="h5">{t("AWVUI-MSG66")}</Typography>
              <CustomList>
                {jobsites?.map((item, index) => (
                  <div key={`wrap-${index}`}>
                    <ListItemButton
                      key={index}
                      selected={item.jobSiteId === jobsiteSelected}
                      onClick={(event) =>
                        handleJobsiteListItemClick(event, item)
                      }
                    >
                      <ListItemText primary={item.jobSiteName} />
                    </ListItemButton>
                    <Divider />
                  </div>
                ))}
              </CustomList>
            </CustomGrid>
            <CustomGrid item xs={4}>
              <Typography variant="h5">{t("AWVUI-MSG184")}</Typography>
              <CustomList>
                {mapInfos.map((item, index) => (
                  <div key={`wrap-${index}`}>
                    <ListItemButton
                      key={index}
                      selected={item.mapId === mapInfoSelected}
                      onClick={(event) =>
                        handleMapInfoListItemClick(event, item)
                      }
                    >
                      <ListItemText primary={item.mapName} />
                    </ListItemButton>
                    <Divider />
                  </div>
                ))}
              </CustomList>
            </CustomGrid>
          </Grid>

          <DialogActions>
            <BorderedButton disabled={disabled} onClick={handleClose}>
              {t("AWVUI-MSG11")}
            </BorderedButton>
          </DialogActions>
        </Dialog>
      )}
      <ErrorDialog
        open={showError}
        message={errMsg}
        positiveButtonText={t("AWVUI-MSG120")}
        onPositiveButtonClick={() => {
          setShowError(false);
          init();
          retry();
        }}
      />
    </>
  );
};

const CustomList = styled(List)(({ theme }) => ({
  backgroundColor: "white",
  margin: "10px 0px 10px 0px",
  width: "100%",
  height: "200px",
  overflow: "auto",
}));

const CustomGrid = styled(Grid)(({ theme }) => ({
  padding: "0 5px",
  justifyContent: "center",
  alignItems: "center",
}));
